import { EventEmitter, OnDestroy } from '@angular/core';
import { Component, Input, OnInit, Output } from '@angular/core';
import _ from 'lodash';
import { combineLatest, Subscription } from 'rxjs';
import { BaseODataService } from '../../services/base-odata.service';
import { FilterGroup, ODataFilter } from '../odata-filter/models/odata-filter';
import { SelectedFilter } from '../odata-filter/models/selected-filter';
import { QuickFilterItem } from './quick-odata-filter-item/quick-odata-filter-item.component';
import { ResponsiveService } from '../../services/responsive.service';

@Component({
  selector: 'quick-odata-filter',
  templateUrl: './quick-odata-filter.component.html',
  styleUrls: ['./quick-odata-filter.component.scss'],
  standalone: false
})
export class QuickODataFilterComponent implements OnInit, OnDestroy {
  @Output() apply = new EventEmitter<SelectedFilter[]>();
  @Output() showAllFilters = new EventEmitter();
  @Input() filterService: BaseODataService;

  filterItems: QuickFilterItem[] = [];
  appliedFilters: SelectedFilter[] = [];
  filterInternallyApplied = false;
  showManageFilters = false;
  filterCollection: ODataFilter[] = [];
  subscriptions: Subscription[] = [];
  leftDrawerOpen = true;

  constructor(private responsiveService: ResponsiveService) {}

  ngOnInit(): void {
    if (!this.filterService) return;
    this.subscriptions.push(
      combineLatest([
        this.filterService.appliedFilters,
        this.filterService.filterCollection,
        this.filterService.quickFilterNames
      ]).subscribe((results) => {
        if (results[0] && results[1] && results[2]) {
          this.updateFilters(results);
        }
      }),
      this.responsiveService.leftDrawerState.subscribe((isOpen) => {
        if (isOpen !== undefined) {
          this.leftDrawerOpen = isOpen;
        }
      })
    );
  }

  updateFilters(
    results: [
      appliedFilters: SelectedFilter[],
      filterCollection: ODataFilter[],
      quickFilterNames: string[]
    ]
  ) {
    if (this.filterInternallyApplied) {
      this.filterInternallyApplied = false;
      return;
    }
    this.appliedFilters = results[0].map((filter) => new SelectedFilter(filter));
    this.filterCollection = results[1];
    this.filterItems = results[2]
      .map((filterName, index) => {
        let item = this.makeFilterItem(
          filterName,
          FilterGroup[
            Object.keys(FilterGroup).find((filterGroup) =>
              filterName.includes(`(${FilterGroup[filterGroup]})`)
            )
          ]
        );
        item.rank = index;
        return item;
      })
      .filter((x) => x.filter);
  }
  private makeFilterItem(filterName: string, filterGroup: FilterGroup) {
    let item: QuickFilterItem;
    let appliedFilter = filterGroup
      ? this.appliedFilters.find(
          (x) =>
            x.filter.displayName == this.getActualName(filterName) &&
            x.filter.filterGroup == filterGroup
        )
      : this.appliedFilters.find((x) => x.filter.displayName == this.getActualName(filterName));

    if (appliedFilter) {
      item = appliedFilter as QuickFilterItem;
      item.isSelected = true;
      item.isFilterApplied = true;
    } else {
      item = new QuickFilterItem(null);
      item.filter = filterGroup
        ? this.filterCollection.find(
            (x) => x.displayName == this.getActualName(filterName) && x.filterGroup == filterGroup
          )
        : this.filterCollection.find((x) => x.displayName == this.getActualName(filterName));
    }
    item.group = filterGroup;
    return item;
  }

  private getActualName(name: string) {
    let actualName = name;
    let filterGroup = Object.keys(FilterGroup).find((filterGroup) =>
      name.includes(`(${FilterGroup[filterGroup]})`)
    );
    if (filterGroup) {
      actualName = name.replace(` (${FilterGroup[filterGroup]})`, '');
    }
    return actualName;
  }

  onAdd(appliedFilter: QuickFilterItem) {
    let index = appliedFilter.group
      ? this.appliedFilters.findIndex(
          (x) =>
            x.filter.displayName == appliedFilter.filter.displayName &&
            x.filter.filterGroup == appliedFilter.group
        )
      : this.appliedFilters.findIndex(
          (x) => x.filter.displayName == appliedFilter.filter.displayName
        );
    if (index != -1) {
      this.appliedFilters[index] = new SelectedFilter(appliedFilter);
    } else {
      this.appliedFilters.push(new SelectedFilter(appliedFilter));
    }
    this.applyFilters();
  }

  onRemove(removedFilter: QuickFilterItem) {
    let index = removedFilter.group
      ? this.appliedFilters.findIndex(
          (x) =>
            x.filter.displayName == removedFilter.filter.displayName &&
            x.filter.filterGroup == removedFilter.group
        )
      : this.appliedFilters.findIndex(
          (x) => x.filter.displayName == removedFilter.filter.displayName
        );
    if (index != -1) {
      this.appliedFilters.splice(index, 1);
      this.applyFilters();
    }
  }

  toggleQuickFiltersClick() {
    this.responsiveService.setLeftDrawerState(!this.leftDrawerOpen);
  }

  applyManageFilterResult(manageFilterResult: string[]) {
    this.showManageFilters = false;
    this.filterService.applyManageFilterResultToView(manageFilterResult);
  }

  private applyFilters() {
    this.filterInternallyApplied = true;
    this.filterService.isFetchingData = true;
    this.filterService.applyFilter(this.appliedFilters);
    if (this.filterService.selectedView) {
      this.filterService.selectedView.isModified = true;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
